import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Item, UserItem } from '../types/items';

export function useItems(walletAddress?: string) {
  const [items, setItems] = useState<Item[]>([]);
  const [userItems, setUserItems] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch marketplace items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('items')
          .select('*')
          .order('price', { ascending: true });

        if (error) throw error;

        // Log the fetched data for debugging
        console.log('Fetched items:', data);

        const formattedItems = data.map(item => ({
          id: item.id,
          name: item.name,
          type: item.type,
          rarity: item.rarity,
          price: item.price,
          imageUrl: item.image_url,
          stats: item.stats
        }));

        setItems(formattedItems);
      } catch (err) {
        console.error('Error fetching items:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch items');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  // Fetch user's items
  useEffect(() => {
    const fetchUserItems = async () => {
      if (!walletAddress) {
        setUserItems([]);
        return;
      }

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('user_items')
          .select(`
            *,
            items:item_id (*)
          `)
          .eq('wallet_address', walletAddress);

        if (error) throw error;

        // Log the fetched user items for debugging
        console.log('Fetched user items:', data);

        const formattedUserItems = data.map(userItem => ({
          ...userItem.items,
          id: userItem.items.id,
          name: userItem.items.name,
          type: userItem.items.type,
          rarity: userItem.items.rarity,
          price: userItem.items.price,
          imageUrl: userItem.items.image_url,
          stats: userItem.items.stats,
          quantity: userItem.quantity,
          createdAt: userItem.created_at
        }));

        setUserItems(formattedUserItems);
      } catch (err) {
        console.error('Error fetching user items:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user items');
      } finally {
        setLoading(false);
      }
    };

    fetchUserItems();
  }, [walletAddress]);

  const refetch = async () => {
    setLoading(true);
    if (walletAddress) {
      await fetchUserItems();
    } else {
      await fetchItems();
    }
  };

  const fetchUserItems = async () => {
    if (!walletAddress) return;
    
    try {
      const { data, error } = await supabase
        .from('user_items')
        .select(`
          *,
          items:item_id (*)
        `)
        .eq('wallet_address', walletAddress);

      if (error) throw error;

      const formattedUserItems = data.map(userItem => ({
        ...userItem.items,
        id: userItem.items.id,
        name: userItem.items.name,
        type: userItem.items.type,
        rarity: userItem.items.rarity,
        price: userItem.items.price,
        imageUrl: userItem.items.image_url,
        stats: userItem.items.stats,
        quantity: userItem.quantity,
        createdAt: userItem.created_at
      }));

      setUserItems(formattedUserItems);
    } catch (err) {
      console.error('Error fetching user items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user items');
    }
  };

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('items')
        .select('*')
        .order('price', { ascending: true });

      if (error) throw error;

      const formattedItems = data.map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        rarity: item.rarity,
        price: item.price,
        imageUrl: item.image_url,
        stats: item.stats
      }));

      setItems(formattedItems);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
    }
  };

  return {
    items,
    userItems,
    loading,
    error,
    refetch
  };
}